import React, { useState, useEffect, memo, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@material-ui/core/styles'
import {
  KeyboardRounded as ShortcutIcon,
  AddCircleRounded as AddIcon,
  EditRounded as EditIcon,
  FileCopyRounded as FileIcon,
  DeleteRounded as DeleteIcon,
} from '@material-ui/icons'
import { useSnackbar } from 'notistack'

import api from '../../../../services/api'

import {
  shortcutError,
  deleteShortcut,
  deletedShortcut,
  resetShortcutStatus,
} from '../../../../redux/shortcuts/actions'

import IconButton from '../../../atoms/inputs/iconButton'
import HighlightSearch from '../../../atoms/display/highlightSearch'
import Icon from '../../../atoms/display/icon'
import Typography from '../../../atoms/display/typography'
import Alert from '../../../molecules/feedback/dialogs/alert'
import ShortcutDialog from '../../../organisms/dialogs/shortcut'

import useStyles from './style'
import { removeSpecials } from '../../../../utils/customMethods'
import TooltipInfo from '../tooltipInfo'

const ShortcutsComponent = ({ keyword, ...props }) => {
  const shortcuts = useSelector((state) => state.shortcuts)
  const classes = useStyles()
  const theme = useTheme()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [openCloseShortcutDialog, setOpenCloseShortcutDialog] = useState(false)
  const [openCloseAlert, setOpenCloseAlert] = useState(false)
  const [shortcutId, setShortcutId] = useState(null)

  var activeConversation = useSelector((state) => state.activeConversation)

  const copyToClippboard = (string) => {
    navigator.clipboard.writeText(string).then(
      () => {
        enqueueSnackbar(`${t('commons.copied')}`, {
          variant: 'success',
        })
      },
      (err) => {
        console.error(t('commons.copiedError'), err)
      }
    )
  }

  const shortcutContent = useMemo(() => {
    if (keyword) {
      return (
        shortcuts?.shortcuts?.filter((el) => {
          return (
            removeSpecials(el?.short_name)
              ?.toLowerCase()
              .includes(keyword.toLowerCase()) ||
            removeSpecials(el?.message)
              ?.toLowerCase()
              .includes(keyword.toLowerCase())
          )
        }) || []
      )
    }

    return shortcuts?.shortcuts || []
  }, [shortcuts])

  useEffect(() => {
    if (!(openCloseAlert || openCloseShortcutDialog) && shortcutId)
      setShortcutId(null)
  }, [openCloseAlert, shortcutId, openCloseShortcutDialog])

  const handleDeleteShortchut = async () => {
    dispatch(deleteShortcut(shortcutId))
    try {
      const response = await api.delete(
        `/flash-message${shortcutId ? `?id=${shortcutId}` : ''}`
      )
      if (response.status === 200) {
        const message = response.data?.message
        dispatch(deletedShortcut(shortcutId, message))
        enqueueSnackbar(t('toasts.shortcutDeleted'), {
          variant: 'success',
        })
        localStorage.setItem('undockCommand', 'updateShortcut')
        setOpenCloseAlert(false)
      } else {
        throw response.data
      }
    } catch (error) {
      let message =
        error?.response?.status === 500
          ? 'Unavailable service'
          : [...error?.response?.data?.message]
      dispatch(
        shortcutError({ action: 'delete_shortcut', message: message[0]?.value })
      )
      enqueueSnackbar(t('toasts.actionError'), {
        variant: 'error',
      })
    }
    dispatch(resetShortcutStatus('delete_shortcut'))
  }

  const openDeleteModal = (id) => {
    setShortcutId(id)
    setOpenCloseAlert(true)
  }

  return (
    <>
      <div className={classes.titleContainer}>
        <Icon text={t('buttons.shortcuts')}>
          <ShortcutIcon />
        </Icon>
        <TooltipInfo />
      </div>
      <div
        style={{
          marginTop: 15,
          marginBottom: 15,
        }}
      >
        <button
          className={classes.add}
          onClick={() => setOpenCloseShortcutDialog(true)}
        >
          <AddIcon style={{ marginRight: 10 }} />
          <Typography variant="body1">{t('buttons.add')}</Typography>
        </button>
        {shortcutContent.map((el) => {
          return (
            <div
              className={classes.shortcutContainer + ' search-anchor-tree'}
              onClick={(e) => {
                e.stopPropagation()
                const fieldMessage = document.getElementById(
                  `field-message-${activeConversation}`
                )
                if (fieldMessage) {
                  fieldMessage.value = el.message
                  fieldMessage.focus()
                  fieldMessage.scrollIntoView({
                    inline: 'center',
                    behavior: 'smooth',
                  })
                }
              }}
            >
              <div className={classes.shortcutContent}>
                <div>
                  <Typography
                    variant="h6"
                    style={{
                      color: theme.palette.primary.main,
                      wordBreak: 'break-all',
                    }}
                  >
                    //&nbsp;
                    <HighlightSearch
                      text={el.short_name}
                      keyword={keyword}
                      anchor="faq"
                      copy={false}
                    />
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.descriptionText}
                  >
                    <HighlightSearch
                      text={el.message}
                      keyword={keyword}
                      anchor="faq"
                      copy={false}
                    />
                  </Typography>
                </div>
                <div style={{ width: 10 }} />
                <div className={classes.contentIcons}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShortcutId(el.id)
                      setOpenCloseShortcutDialog(true)
                    }}
                  >
                    <EditIcon
                      style={{
                        color: theme.palette.primary.main,
                        fontSize: 14,
                        margin: 3,
                      }}
                    />
                  </IconButton>
                  <div style={{ height: 5 }} />
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      copyToClippboard(el.message)
                    }}
                  >
                    <FileIcon
                      style={{
                        color: theme.palette.primary.main,
                        fontSize: 14,
                        margin: 3,
                      }}
                    />
                  </IconButton>
                  <div style={{ height: 5 }} />
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      openDeleteModal(el.id)
                    }}
                  >
                    <DeleteIcon
                      style={{
                        color: theme.palette.primary.main,
                        fontSize: 14,
                        margin: 3,
                      }}
                    />
                  </IconButton>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {openCloseShortcutDialog && (
        <ShortcutDialog
          open={setOpenCloseShortcutDialog}
          shortcutId={shortcutId}
        />
      )}
      {openCloseAlert && (
        <Alert
          open={setOpenCloseAlert}
          setOpen={setOpenCloseAlert}
          title={t('commons.question')}
          text={t('alerts.removeShortcut')}
          agreeAction={() => handleDeleteShortchut()}
          loading={shortcuts?.status?.delete_shortcut?.loading}
        />
      )}
    </>
  )
}

export default memo(ShortcutsComponent)
